import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useTransform, useSpring } from 'framer-motion';
import { Menu, X, Instagram, Facebook, Youtube } from 'lucide-react';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import LanguageSwitch from './LanguageSwitch';


// Magnetic button wrapper — subtly follows cursor on hover
const MagneticButton = ({ children, strength = 10, className = '', ...props }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });

  const springX = useSpring(0, { stiffness: 200, damping: 20, mass: 0.3 });
  const springY = useSpring(0, { stiffness: 200, damping: 20, mass: 0.3 });

  useEffect(() => {
    springX.set(pos.x);
    springY.set(pos.y);
  }, [pos, springX, springY]);

  const handleMouseMove = (e) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - (rect.left + rect.width / 2);
    const y = e.clientY - (rect.top + rect.height / 2);
    const clampedX = Math.max(-strength, Math.min(strength, x * 0.35));
    const clampedY = Math.max(-strength, Math.min(strength, y * 0.35));
    setPos({ x: clampedX, y: clampedY });
  };

  const handleMouseLeave = () => setPos({ x: 0, y: 0 });

  return (
    <motion.div
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ x: springX, y: springY }}
      className={`inline-block ${className}`}
      {...props}
    >
      {children}
    </motion.div>
  );
};

// Animated nav link with shared layout underline
const NavLink = ({ item, isActive, activeId }) => (
  <Link
    to={item.href}
    className="relative group px-1 py-2"
    data-testid={`nav-${item.href.replace('/', '') || 'home'}-link`}
  >
    <motion.span
      className={`relative text-xs uppercase tracking-[0.18em] font-medium transition-colors duration-300 ${
        isActive ? 'text-[#C9A84C]' : 'text-[#B9B2A8] group-hover:text-[#F5F0E8]'
      }`}
    >
      {item.label}
    </motion.span>

    {/* Hover underline — slides in from left */}
    <span
      className="absolute left-0 right-0 -bottom-0.5 h-[1.5px] overflow-hidden pointer-events-none"
      aria-hidden="true"
    >
      <span
        className={`block h-full bg-gradient-to-r from-[#C9A84C] via-[#E2C56A] to-[#C9A84C] origin-left transform transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
          isActive ? 'scale-x-0' : 'scale-x-0 group-hover:scale-x-100'
        }`}
      />
    </span>

    {/* Active underline — shared layoutId for morph between links */}
    {isActive && (
      <motion.span
        layoutId={activeId}
        className="absolute left-0 right-0 -bottom-0.5 h-[1.5px] bg-gradient-to-r from-transparent via-[#C9A84C] to-transparent"
        transition={{ type: 'spring', stiffness: 380, damping: 30 }}
      />
    )}
  </Link>
);

const Navbar = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { scrollY, scrollYProgress } = useScroll();
  const progressScaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 24, mass: 0.2 });

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolled(latest > 40);
  });

  // Logo scale: 60px at top, shrinks to 40px on scroll (desktop)
  const desktopLogoHeight = useTransform(scrollY, [0, 120], [60, 40], { clamp: true });
  const mobileLogoHeight = useTransform(scrollY, [0, 120], [48, 36], { clamp: true });

  const navItems = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.agenda'), href: '/agenda' },
    { label: t('nav.histoire'), href: '/histoire' },
    { label: t('nav.videos'), href: '/videos' },
    { label: t('nav.galerie'), href: '/galerie' },
    { label: t('nav.ideologie'), href: '/ideologie' },
    { label: t('nav.experience'), href: '/experience' },
  ];

  const isActive = (href) => location.pathname === href;

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Entrance animation variants
  const navContainerVariants = {
    hidden: { y: -100, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1],
        when: 'beforeChildren',
        staggerChildren: 0.06,
        delayChildren: 0.2,
      },
    },
  };

  const childFade = {
    hidden: { y: -12, opacity: 0 },
    visible: { y: 0, opacity: 1, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.85 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
  };

  // Mobile menu stagger
  const mobileContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.15,
        staggerChildren: 0.06,
      },
    },
  };

  const mobileItem = {
    hidden: { x: 40, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
    },
  };

  return (
    <>
      {/* Scroll progress bar — gold, ultra-thin */}
      <motion.div
        aria-hidden="true"
        style={{ scaleX: progressScaleX, transformOrigin: '0% 50%' }}
        className="fixed top-0 inset-x-0 z-[60] h-[2px] bg-gradient-to-r from-[#C9A84C] via-[#E2C56A] to-[#C9A84C] pointer-events-none"
      />

      <motion.nav
        variants={navContainerVariants}
        initial="hidden"
        animate="visible"
        className={`fixed top-0 inset-x-0 z-50 ${
          scrolled
            ? 'bg-black/60 backdrop-blur-xl border-b border-white/10 shadow-[0_10px_40px_-10px_rgba(201,168,76,0.15)]'
            : 'bg-gradient-to-b from-black/40 via-black/10 to-transparent backdrop-blur-[2px] border-b border-transparent'
        }`}
        style={{
          transitionProperty: 'background-color, backdrop-filter, border-color, box-shadow',
          transitionDuration: '400ms',
          transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
        }}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className={`flex items-center justify-between transition-[height] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] ${
              scrolled ? 'h-16 lg:h-20' : 'h-20 lg:h-24'
            }`}
          >
            {/* Logo */}
            <motion.div variants={logoVariants} className="flex-shrink-0">
              <Link to="/" className="flex items-center" data-testid="nav-logo">
                {/* Desktop logo */}
                <motion.img
                  src="/images/logo.png"
                  alt="Invasion Latina"
                  style={{ height: desktopLogoHeight }}
                  className="hidden sm:block w-auto drop-shadow-[0_2px_10px_rgba(201,168,76,0.25)] transition-[filter] duration-500 hover:drop-shadow-[0_2px_16px_rgba(226,197,106,0.45)]"
                />
                {/* Mobile logo */}
                <motion.img
                  src="/images/logo.png"
                  alt="Invasion Latina"
                  style={{ height: mobileLogoHeight }}
                  className="sm:hidden w-auto drop-shadow-[0_2px_10px_rgba(201,168,76,0.25)]"
                />
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <motion.div variants={childFade} className="hidden lg:flex items-center gap-7">
              {navItems.map((item) => (
                <NavLink
                  key={item.href}
                  item={item}
                  isActive={isActive(item.href)}
                  activeId="nav-active-underline"
                />
              ))}
            </motion.div>

            {/* Right Side: Language + CTAs */}
            <motion.div variants={childFade} className="hidden lg:flex items-center gap-3">
              <LanguageSwitch />

              <Link to="/booking" data-testid="nav-vip-booking-link">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-[11px] uppercase tracking-[0.18em] border-[#C9A84C]/40 text-[#C9A84C] bg-transparent hover:bg-[#C9A84C]/10 hover:border-[#C9A84C]/70 hover:text-[#E2C56A] transition-all duration-300 rounded-full px-5 font-medium"
                >
                  {t('nav.booking')}
                </Button>
              </Link>

              <MagneticButton strength={10}>
                <a
                  href="/#tickets"
                  data-testid="nav-tickets-cta"
                  className="relative inline-block group"
                >
                  {/* Gold glow — intensifies on hover */}
                  <span
                    aria-hidden="true"
                    className="absolute inset-0 rounded-full bg-[#C9A84C] blur-xl opacity-40 group-hover:opacity-70 transition-opacity duration-500"
                  />
                  <Button
                    size="sm"
                    className="relative text-[11px] uppercase tracking-[0.18em] bg-gradient-to-br from-[#E2C56A] via-[#C9A84C] to-[#A88835] text-[#060606] hover:shadow-[0_0_30px_rgba(201,168,76,0.5)] border border-[#E2C56A]/50 rounded-full px-6 py-2 font-semibold transition-all duration-300"
                  >
                    {t('nav.tickets')}
                  </Button>
                </a>
              </MagneticButton>
            </motion.div>

            {/* Mobile Menu Button */}
            <motion.div variants={childFade} className="flex lg:hidden items-center gap-2">
              <LanguageSwitch />
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-[#F5F0E8] hover:bg-white/5 rounded-full"
                    data-testid="mobile-menu-button"
                    aria-label="Open menu"
                  >
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>

                <SheetContent
                  side="right"
                  className="bg-[#060606]/90 backdrop-blur-2xl border-l border-white/10 w-[320px] sm:w-[380px] p-0 overflow-hidden"
                >
                  {/* Decorative gold accent */}
                  <div
                    aria-hidden="true"
                    className="absolute top-0 right-0 h-[200px] w-[200px] bg-[#C9A84C]/10 rounded-full blur-3xl pointer-events-none"
                  />
                  <div
                    aria-hidden="true"
                    className="absolute bottom-0 left-0 h-[300px] w-[300px] bg-[#C9A84C]/5 rounded-full blur-3xl pointer-events-none"
                  />

                  <div className="relative flex flex-col h-full">
                    {/* Header: logo + close */}
                    <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                      <img
                        src="/images/logo.png"
                        alt="Invasion Latina"
                        className="h-10 w-auto"
                      />
                      <button
                        onClick={() => setMobileOpen(false)}
                        className="h-10 w-10 rounded-full flex items-center justify-center text-[#F5F0E8] hover:bg-white/10 transition-colors duration-200"
                        aria-label="Close menu"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    {/* Animated nav items */}
                    <AnimatePresence>
                      {mobileOpen && (
                        <motion.div
                          variants={mobileContainer}
                          initial="hidden"
                          animate="visible"
                          exit="hidden"
                          className="flex-1 flex flex-col px-6 py-6 overflow-y-auto"
                        >
                          <nav className="flex flex-col">
                            {navItems.map((item) => {
                              const active = isActive(item.href);
                              return (
                                <motion.div key={item.href} variants={mobileItem}>
                                  <Link
                                    to={item.href}
                                    onClick={() => setMobileOpen(false)}
                                    className="group relative flex items-center py-4 border-b border-white/5"
                                    data-testid={`mobile-nav-${item.href.replace('/', '') || 'home'}-link`}
                                  >
                                    <span
                                      className={`font-display text-2xl uppercase tracking-wider transition-colors duration-300 ${
                                        active
                                          ? 'text-[#C9A84C]'
                                          : 'text-[#F5F0E8] group-hover:text-[#C9A84C]'
                                      }`}
                                    >
                                      {item.label}
                                    </span>
                                    {active && (
                                      <motion.span
                                        layoutId="mobile-active-dot"
                                        className="ml-auto h-1.5 w-1.5 rounded-full bg-[#C9A84C] shadow-[0_0_10px_rgba(201,168,76,0.8)]"
                                      />
                                    )}
                                  </Link>
                                </motion.div>
                              );
                            })}
                          </nav>

                          {/* CTAs */}
                          <motion.div variants={mobileItem} className="mt-8 flex flex-col gap-3">
                            <Link
                              to="/booking"
                              onClick={() => setMobileOpen(false)}
                              data-testid="mobile-nav-booking-link"
                            >
                              <Button
                                variant="outline"
                                className="w-full border-[#C9A84C]/50 text-[#C9A84C] bg-transparent hover:bg-[#C9A84C]/10 font-display uppercase tracking-[0.18em] rounded-full py-6 text-sm"
                              >
                                {t('nav.booking')}
                              </Button>
                            </Link>
                            <a
                              href="/#tickets"
                              onClick={() => setMobileOpen(false)}
                              data-testid="mobile-nav-tickets-cta"
                            >
                              <Button
                                className="w-full bg-gradient-to-br from-[#E2C56A] via-[#C9A84C] to-[#A88835] text-[#060606] hover:shadow-[0_0_25px_rgba(201,168,76,0.5)] font-display uppercase tracking-[0.18em] font-semibold rounded-full py-6 text-sm"
                              >
                                {t('nav.tickets')}
                              </Button>
                            </a>
                          </motion.div>

                          {/* Socials */}
                          <motion.div
                            variants={mobileItem}
                            className="mt-auto pt-8 flex items-center justify-center gap-5"
                          >
                            <a
                              href="https://www.instagram.com/invasion_latina_official/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-[#B9B2A8] hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all duration-300"
                              aria-label="Instagram"
                            >
                              <Instagram className="h-5 w-5" />
                            </a>
                            <a
                              href="https://www.facebook.com/invasionlatinaofficial"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-[#B9B2A8] hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all duration-300"
                              aria-label="Facebook"
                            >
                              <Facebook className="h-5 w-5" />
                            </a>
                            <a
                              href="https://www.youtube.com/@invasionlatina"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="h-11 w-11 rounded-full border border-white/15 flex items-center justify-center text-[#B9B2A8] hover:text-[#C9A84C] hover:border-[#C9A84C]/50 transition-all duration-300"
                              aria-label="YouTube"
                            >
                              <Youtube className="h-5 w-5" />
                            </a>
                          </motion.div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </SheetContent>
              </Sheet>
            </motion.div>
          </div>
        </div>
      </motion.nav>
    </>
  );
};

export default Navbar;
